import {
  generatePath,
  useNavigate
} from "react-router-dom";
import {useSelector} from "react-redux";
import React, {useEffect} from "react";
import BreadcrumbLayout
  from '@/components/common/CustomLayout/BreadcrumbLayout'
import Table from '@/components/common/Table'
import Button from '@/components/common/Button'
import {ReactComponent as RiAddLine} from '@/assets/icons/ri-add-line.svg'
import {
  ReactComponent as RiMapPinFill
} from '@/assets/icons/ri-map-pin-fill.svg'
import {ReactComponent as RiEditLine} from '@/assets/icons/ri-edit-line.svg'
import {RootState, useAppDispatch} from "@/store";
import {
  constructTableMetaFromPaging2
} from "@/utils/paging";
import {getVenuesTemp} from "@/store/venue-summary-temp/action";
import useCustomPaging from "@/hooks/useCustomPaging";
import useSearchFilter from "@/hooks/useSearchFilter";
import {dynamicAttr} from "@/utils/fallback";

const HEADERS: TableHeader[] = [
  {
    label: 'Venue Name',
    key: 'name',
  },
  {
    label: 'WA Number',
    key: 'pic_phone_number',
  },
  {
    label: 'Address',
    key: 'address',
  },
  {
    label: 'Pricing Class',
    key: 'prices',
  },
  {
    label: 'Field Total',
    key: 'courts',
  },
  {
    label: 'Action',
    key: 'action',
  },
];
const SORT: TableSort = {
  type: 'SERVER',
  handler: () => {
  }
}

const renderName = (name: string) => (
  <div className="flex gap-4">
    <div className="text-red-primary">
      <RiMapPinFill/>
    </div>
    <div className="text-jsm font-bold">
      {name}
    </div>
  </div>
)
const renderAddress = (address: VenueRowAddress) => (
  <div className='flex flex-col justify-center items-start'>
    <span className="text-jsm font-normal">{address.address}</span>
    <span
      className='text-jsm text-blue-light font-normal'>{address.address_long}, {address.address_lat}</span>
  </div>
)
const renderPrices = (prices: Price[]) => (
  <ul className="ml-6">
    {prices.map(p => <li className="list-disc"
                         key={p.id + p.name}>
      {p.name}
    </li>)}
  </ul>
)
const renderCourts = (courts: Court[]) => (
  <div>
    {courts.length} Lapangan
  </div>
)
const renderAction = (action: Function) => (
  <Button
    onClick={action}
    type={null}
    variant="secondary"
    className="border-stroke-subtle border px-4"
  >
    <div className="flex gap-2 justify-center items-center">
      <RiEditLine/>
      Edit
    </div>
  </Button>
)

const VenueSummary: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {
    venues,
    paging
  } = useSelector((state: RootState) => state.venueSummaryTemp.state)
  const [
    currentPageFilter,,
    handlePageNumberChange
  ] = useCustomPaging()
  const [
    searchString,
    currentSearchFilter,
    handleChangeSearchString,
    handleSubmitSearch
  ] = useSearchFilter()
  
  const goToCreate = () => {
    navigate('/dashboard/venue/add')
  }
  const goToEditVenue = (venueId: string) => {
    const path = generatePath('/dashboard/venue/edit/:venueId', {venueId})
    navigate(path)
  }
  const getRowValue =
    ({
       id,
       address,
       address_lat,
       address_long,
       ...rest
     }: Venue) => ({
      ...rest,
      address: {
        address,
        address_lat,
        address_long
      } as VenueRowAddress,
      action: () => {
        goToEditVenue(id.toString())
      }
    } as VenueRow)
  const getTableBodyData = () => venues.map((v: Venue) => ({
    id: v.id.toString(),
    value: getRowValue(v) as VenueRow
  }))
  const getTableBody = () => ({
    data: getTableBodyData(),
    component: {
      name: renderName,
      address: renderAddress,
      prices: renderPrices,
      courts: renderCourts,
      action: renderAction
    }
  } as TableBody)
  const getData = () => {
    const params = {
      size: 10,
      ...dynamicAttr('page', currentPageFilter),
      ...dynamicAttr('search', currentSearchFilter)
    }
    dispatch(getVenuesTemp(params))
  }
  
  useEffect(() => {
    getData()
  }, [
    currentPageFilter,
    currentSearchFilter
  ])
  
  return (
    <BreadcrumbLayout>
      <div className="w-full flex flex-col justify-between items-start gap-9">
        <div className="w-full flex justify-between items-center">
          <span className="text-j2xl">Venue</span>
          <div
            className="flex gap-4 justify-between">
            <input
              value={searchString}
              onChange={handleChangeSearchString}
              onKeyDown={handleSubmitSearch}
              name="searchString"
              type="text"
              className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
              placeholder="Search for Venue"/>
            <div className="my-2 border-r-2 border-r-stroke-subtle"/>
            <button type="submit" className="hidden">test</button>
            <Button
              onClick={goToCreate}
              type={null}
              variant="primary"
              className="whitespace-nowrap flex gap-2 pl-4 py-2 justify-center items-center">
              <div className="w-[18px]">
                <RiAddLine/>
              </div>
              <span className="text-jsm">Add Venue</span>
            </Button>
          </div>
        </div>
        {
          venues && <Table
            headers={HEADERS}
            body={getTableBody()}
            sort={SORT}
            meta={constructTableMetaFromPaging2(paging)}
            pageNumberHandler={handlePageNumberChange}/>
        }
      </div>
    </BreadcrumbLayout>
  )
}

export default VenueSummary;
